import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    collection,
    query,
    orderBy,
    getDocs,
    doc,
    getDoc
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useAuth } from '../../../../Contexts/AuthContext';
import {
    SearchIcon,
    EyeIcon,
    ClockIcon,
    StarIcon,
    TagIcon
} from '@heroicons/react/outline';

// Simple in-memory cache (resets on full page reload)
let applicationsCache = null;

const ManageApplications = () => {
    const { userData } = useAuth();
    const [applications, setApplications] = useState([]);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryStats, setCategoryStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'evaluated'
    const [selectedCategory, setSelectedCategory] = useState('all'); // 'all' | specific category
    const [currentPage, setCurrentPage] = useState(1);
    const [applicationsPerPage] = useState(10);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            await Promise.all([
                fetchApplications(),
                fetchCategories()
            ]);
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (applications.length > 0 && categories.length > 0) {
            calculateCategoryStats();
        }
    }, [applications, categories]); // eslint-disable-line react-hooks/exhaustive-deps

    // Set initial selectedCategory based on user role
    useEffect(() => {
        if (userData && categoryStats.length > 0) {
            if (!selectedCategory || (categoryStats.find(cat => cat.categoryName === selectedCategory) === undefined)) {
                // Select the first available category
                setSelectedCategory(categoryStats[0].categoryName);
            }
        }
    }, [userData, categoryStats, selectedCategory]);

    const fetchCategories = async () => {
        try {
            const categoriesDocRef = doc(db, 'hackathonCategories', 'categories');
            const categoriesDoc = await getDoc(categoriesDocRef);

            if (categoriesDoc.exists()) {
                const categoriesData = categoriesDoc.data();
                setCategories(categoriesData.categories || []);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        }
    };

    const getUserAssignedCategories = useCallback(() => {
        if (!userData || userData.role !== 'Judge') return [];

        let assignedCategories = [];

        // Handle new multi-category assignment structure
        if (userData.categoryAssignments && Array.isArray(userData.categoryAssignments)) {
            assignedCategories = userData.categoryAssignments.map(assignment => assignment.category);
        }
        // Handle old single assignment structure (backward compatibility)
        else if (userData.categoryAssignment) {
            assignedCategories = [userData.categoryAssignment.category];
        }

        return assignedCategories;
    }, [userData]);

    const getVisibleCategories = useCallback(() => {
        if (!userData) return [];

        if (userData.role === 'Administrator') {
            return categories;
        } else if (userData.role === 'Judge') {
            const assignedCategoryNames = getUserAssignedCategories();
            return categories.filter(category => assignedCategoryNames.includes(category.categoryName));
        }

        return [];
    }, [userData, categories, getUserAssignedCategories]);

    const calculateCategoryStats = useCallback(() => {
        const visibleCategories = getVisibleCategories();

        const stats = visibleCategories.map(category => {
            const categoryApplications = applications.filter(app =>
                app.category === category.categoryName
            );

            const totalApplications = categoryApplications.length;
            const pendingEvaluation = categoryApplications.filter(app => !app.isEvaluated).length;
            const evaluated = categoryApplications.filter(app => app.isEvaluated).length;

            return {
                ...category,
                totalApplications,
                pendingEvaluation,
                evaluated
            };
        });

        setCategoryStats(stats);
    }, [applications, getVisibleCategories]);

    const fetchApplications = async () => {
        try {
            // Serve from cache if present
            if (applicationsCache) {
                setApplications(applicationsCache);
                setLoading(false);
                return;
            }

            setLoading(true);
            // Try both collection names to handle different naming conventions
            let applicationsData = [];

            try {
                const applicationsRef = collection(db, 'hackathonApplications');
                const q = query(applicationsRef, orderBy('submissionDate', 'desc'));
                const querySnapshot = await getDocs(q);

                querySnapshot.forEach((doc) => {
                    applicationsData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
            } catch (error) {
                // If hackathonApplications doesn't exist, try hackathonApplications
                console.log('Trying hackathonApplications collection...');
                const applicationsRef = collection(db, 'hackathonApplications');
                const q = query(applicationsRef, orderBy('submissionDate', 'desc'));
                const querySnapshot = await getDocs(q);

                querySnapshot.forEach((doc) => {
                    applicationsData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
            }

            setApplications(applicationsData);
            applicationsCache = applicationsData; // cache it
        } catch (error) {
            console.error('Error fetching applications:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterApplications = useCallback(() => {
        let filtered = [...applications];

        // Role-based filtering
        if (userData && userData.role === 'Judge') {
            const assignedCategories = getUserAssignedCategories();
            filtered = filtered.filter(app => assignedCategories.includes(app.category));
        }

        // Category filter
        if (selectedCategory) {
            filtered = filtered.filter(app => app.category === selectedCategory);
        }

        // Filter by status using single isEvaluated field
        switch (statusFilter) {
            case 'pending':
                filtered = filtered.filter(app => !app.isEvaluated);
                break;
            case 'evaluated':
                filtered = filtered.filter(app => app.isEvaluated);
                break;
            case 'all':
            default:
                break;
        }

        // Filter by search term
        if (searchTerm.trim()) {
            filtered = filtered.filter(app =>
                app.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.teamName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.teamLeaderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.teamLeaderEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.email?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort: evaluated applications first (by score descending), then unevaluated
        filtered.sort((a, b) => {
            // If both evaluated, sort by score descending
            if (a.isEvaluated && b.isEvaluated) {
                const scoreA = a.evaluation?.score || 0;
                const scoreB = b.evaluation?.score || 0;
                return scoreB - scoreA;
            }
            // Evaluated comes before unevaluated
            if (a.isEvaluated && !b.isEvaluated) return -1;
            if (!a.isEvaluated && b.isEvaluated) return 1;
            // If both unevaluated, maintain order
            return 0;
        });

        setFilteredApplications(filtered);
        setCurrentPage(1); // Reset to first page when filtering
    }, [applications, searchTerm, statusFilter, selectedCategory, userData, getUserAssignedCategories]);

    useEffect(() => {
        filterApplications();
    }, [filterApplications]);

    const getStatusBadge = (application) => {
        if (application.isEvaluated) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <StarIcon className="w-3 h-3 mr-1" />
                    Evaluated
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                <ClockIcon className="w-3 h-3 mr-1" />
                Pending Evaluation
            </span>
        );
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleViewApplication = (application) => {
        navigate(`/eventdashboard/application-details/${application.id}`, {
            state: { application }
        });
    };

    // Pagination
    const indexOfLastApplication = currentPage * applicationsPerPage;
    const indexOfFirstApplication = indexOfLastApplication - applicationsPerPage;
    const currentApplications = filteredApplications.slice(indexOfFirstApplication, indexOfLastApplication);
    const totalPages = Math.ceil(filteredApplications.length / applicationsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Manage Applications</h1>
                <p className="text-gray-600">
                    {userData?.role === 'Administrator'
                        ? 'Review and manage all hackathon applications across all categories'
                        : 'Review and manage applications for your assigned categories'
                    }
                </p>
            </div>

            {/* Category Statistics Cards */}
            {categoryStats.length > 0 && (
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Categories Overview</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {categoryStats.map((category) => (
                            <div
                                key={category.categoryName}
                                className={`bg-white rounded-lg p-8 shadow-sm border cursor-pointer transition-all hover:shadow-md min-h-[180px] ${selectedCategory === category.categoryName
                                    ? 'border-custom-blue-1 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                onClick={() => setSelectedCategory(category.categoryName)}
                            >
                                <div className="flex items-start justify-between h-full">
                                    <div className="flex-1 min-w-0 flex flex-col">
                                        <p className="text-sm font-medium text-gray-600 mb-4 leading-relaxed">
                                            {category.categoryName}
                                        </p>
                                        <div className="mt-auto grid grid-cols-3 gap-4">
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-custom-blue-1">{category.totalApplications}</p>
                                                <p className="text-xs text-gray-500 mt-1">Total</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-orange-500">{category.pendingEvaluation}</p>
                                                <p className="text-xs text-gray-500 mt-1">Pending</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-purple-600">{category.evaluated}</p>
                                                <p className="text-xs text-gray-500 mt-1">Evaluated</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ml-3 flex-shrink-0">
                                        <div className="p-2 rounded-lg bg-blue-50">
                                            <TagIcon className="h-6 w-6 text-custom-blue-1" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters and Search */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search by project name, team name, or leader email..."
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Category Filter */}
                <div className="relative">
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                    >
                        {categoryStats.map((category) => (
                            <option key={category.categoryName} value={category.categoryName}>
                                {category.categoryName}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Status Filter */}
                <div className="relative flex items-center gap-3">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                    >
                        <option value="all">All Applications</option>
                        <option value="pending">Pending Evaluation</option>
                        <option value="evaluated">Evaluated</option>
                    </select>
                    <button
                        type="button"
                        onClick={async () => {
                            applicationsCache = null;
                            setLoading(true);
                            await fetchApplications();
                            await fetchCategories();
                        }}
                        className="whitespace-nowrap inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white hover:bg-gray-50 text-gray-700 shadow-sm"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Results Summary */}
            <div className="mb-4">
                <p className="text-sm text-gray-600">
                    Showing {currentApplications.length} of {filteredApplications.length} applications
                </p>
            </div>

            {/* Applications Table */}
            <div className="bg-white shadow-sm overflow-hidden rounded-lg border">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Rank
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Team Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Team Lead
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Score
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Submission Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentApplications.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                                        No applications found
                                    </td>
                                </tr>
                            ) : (
                                currentApplications.map((application, index) => {
                                    const rank = (currentPage - 1) * applicationsPerPage + index + 1;
                                    const score = application.evaluation?.score || 0;
                                    const isEvaluated = application.isEvaluated;

                                    return (
                                        <tr key={application.id} className="hover:bg-gray-50">
                                            {/* Rank Column */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    {isEvaluated && rank <= 5 ? (
                                                        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 shadow-lg' :
                                                                rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-500 shadow-md' :
                                                                    rank === 3 ? 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-md' :
                                                                        rank === 4 ? 'bg-blue-100 border-2 border-blue-300' :
                                                                            'bg-green-100 border-2 border-green-300'
                                                            }`}>
                                                            <span className={`text-lg font-bold ${rank === 1 ? 'text-white' :
                                                                    rank === 2 ? 'text-white' :
                                                                        rank === 3 ? 'text-white' :
                                                                            rank === 4 ? 'text-blue-700' :
                                                                                'text-green-700'
                                                                }`}>
                                                                {rank}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm font-medium text-gray-600">
                                                            {isEvaluated ? `#${rank}` : '—'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Team Name Column */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {application.teamName || 'N/A'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {application.category && `${application.category}`}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Team Lead Column */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {application.teamLeaderName || application.fullName || 'N/A'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        Team Lead • {application.teamLeaderEmail || application.email || 'N/A'}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Score Column */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {isEvaluated ? (
                                                    <div className="flex items-center">
                                                        <div className={`text-2xl font-bold ${score >= 80 ? 'text-green-600' :
                                                                score >= 60 ? 'text-blue-600' :
                                                                    score >= 40 ? 'text-yellow-600' :
                                                                        'text-red-600'
                                                            }`}>
                                                            {score}
                                                        </div>
                                                        <span className="text-sm text-gray-500 ml-1">/100</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-400">Pending</span>
                                                )}
                                            </td>

                                            {/* Status Column */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(application)}
                                            </td>

                                            {/* Submission Date Column */}
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(application.submissionDate || application.createdAt)}
                                            </td>

                                            {/* Actions Column */}
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => handleViewApplication(application)}
                                                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-custom-blue-1 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue-1"
                                                >
                                                    <EyeIcon className="w-4 h-4 mr-1" />
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{indexOfFirstApplication + 1}</span> to{' '}
                                <span className="font-medium">
                                    {Math.min(indexOfLastApplication, filteredApplications.length)}
                                </span>{' '}
                                of <span className="font-medium">{filteredApplications.length}</span> results
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages)].map((_, index) => (
                                    <button
                                        key={index + 1}
                                        onClick={() => paginate(index + 1)}
                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === index + 1
                                            ? 'z-10 bg-blue-50 border-custom-blue-1 text-custom-blue-1'
                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                            }`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageApplications;
